import { lazy, Suspense, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, BarChart3, CheckCircle2, Download, FileText, Loader2 } from 'lucide-react';
import { Role } from '@nexa/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { RoleGate } from '@/auth/role-gate';
import { DisruptionDossierDialog } from '@/components/documents/disruption-dossier-sheet';
import { fmtDateTime } from '@/lib/documents/format';
import { localized, num, p50p95, pct, priceText } from '@/lib/reports/format';
import type { ReportExport, ReportFilters, ReportMetricKey, ReportsOverview } from '@/lib/reports/types';
import { useReportDownload, useReportsOverview } from '@/lib/reports/use-reports';

// /reports (#823, SKY PoC §8) — REAL data only. Every card, series and row is
// folded by the BFF from the per-passenger reconciliation (#807) and the operator
// activity (#813) of the tenant's disruptions in the window; the NEX-171 synthetic
// L1–L4 series are gone. An empty tenant shows an empty state, never an invented
// chart. Each metric prints its definition (shipped in the payload) beside the value.
// Money is the airline's price (commercial-contract M3, converted by the BFF); without a
// price card the amounts read «No disponible» and a notice says why.
//
// Charts are lazy-imported so Recharts stays out of the entry chunk (TDS §11.1).

const Charts = lazy(() => import('./reports-charts'));

const MAX_COMPOSED = 40;

function defaultFilters(): ReportFilters {
  const today = new Date();
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60_000);
  return {
    from: thirtyDaysAgo.toISOString().slice(0, 10),
    to: today.toISOString().slice(0, 10),
  };
}

export function ReportsRoute() {
  return (
    <RoleGate min={Role.OPS_SUPERVISOR} hideWhenDenied>
      <ReportsPage />
    </RoleGate>
  );
}

/** Mounted only past the role gate, so a denied operator never fires the composition. */
function ReportsPage() {
  const { t } = useTranslation('admin');
  const [filters, setFilters] = useState<ReportFilters>(defaultFilters);
  const query = useReportsOverview(filters);

  return (
      <main className="density-page mx-auto flex max-w-6xl flex-col gap-4 p-6">
        <header>
          <h1 className="text-2xl font-semibold">{t('common:nav.reports')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('reports.subtitle')}</p>
        </header>

        <div className="flex flex-wrap items-end justify-between gap-3">
          <DateRangeFilter value={filters} onChange={setFilters} />
          <div className="flex flex-wrap gap-2">
            <ExportButton kind="overview" filters={filters} disabled={!query.data || query.data.disruptions.length === 0} />
            <ExportButton kind="daily" filters={filters} disabled={!query.data || query.data.disruptions.length === 0} />
            <ExportButton kind="operators" filters={filters} disabled={!query.data || query.data.operators.length === 0} />
          </div>
        </div>

        {query.isLoading ? (
          <div className="space-y-3" data-testid="reports-loading">
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : query.isError || !query.data ? (
          <p className="text-sm text-destructive" role="alert" data-testid="reports-error">
            {t('reports.loadError')}
          </p>
        ) : (
          <OverviewBody overview={query.data} />
        )}
      </main>
  );
}

function DateRangeFilter({ value, onChange }: { value: ReportFilters; onChange: (v: ReportFilters) => void }) {
  const { t } = useTranslation('admin');
  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-medium text-foreground/80">{t('reports.filters.from')}</span>
        <input type="date" value={value.from ?? ''} onChange={(e) => onChange({ ...value, from: e.target.value })} className="rounded-md border border-input bg-background px-2 py-1 text-sm" />
      </label>
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-medium text-foreground/80">{t('reports.filters.to')}</span>
        <input type="date" value={value.to ?? ''} onChange={(e) => onChange({ ...value, to: e.target.value })} className="rounded-md border border-input bg-background px-2 py-1 text-sm" />
      </label>
    </div>
  );
}

function ExportButton({ kind, filters, disabled }: { kind: ReportExport; filters: ReportFilters; disabled: boolean }) {
  const { t } = useTranslation('admin');
  const download = useReportDownload(kind, filters);
  return (
    <Button variant="outline" size="sm" disabled={disabled || download.isPending} onClick={() => download.mutate()} data-testid={`export-${kind}`}>
      {download.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden /> : <Download className="mr-1 h-3 w-3" aria-hidden />}
      {t(`reports.export.${kind}`)}
    </Button>
  );
}

function OverviewBody({ overview }: { overview: ReportsOverview }) {
  const { t } = useTranslation('admin');
  if (!overview.known.disruptions) {
    return (
      <p className="flex items-center gap-2 rounded-md border border-amber-500/60 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="alert" data-testid="reports-unknown">
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
        {t('reports.unknownList')}
      </p>
    );
  }
  if (overview.disruptions.length === 0) {
    return (
      <div data-testid="reports-empty">
        <EmptyState icon={<BarChart3 className="h-8 w-8" aria-hidden />} title={t('reports.empty.title')} description={t('reports.empty.description', { from: overview.window.from, to: overview.window.to })} className="py-16" />
      </div>
    );
  }
  return (
    <>
      {overview.truncated ? (
        <p className="text-xs text-muted-foreground" data-testid="reports-truncated">
          {t('reports.truncated', { max: MAX_COMPOSED })}
        </p>
      ) : null}
      {!overview.priced ? <UnpricedNotice testId="reports-unpriced" /> : null}
      <MetricCards overview={overview} />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardContent className="p-4">
            <h2 className="mb-2 text-sm font-semibold">{t('reports.charts.volumeTitle')}</h2>
            <Suspense fallback={<Skeleton className="h-60 w-full" />}>
              <Charts kind="volume" data={overview.daily} />
            </Suspense>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <h2 className="mb-2 text-sm font-semibold">{t('reports.charts.priceTitle')}</h2>
            {overview.daily.some((d) => d.hotelUsd !== null || d.transportUsd !== null || d.mealUsd !== null) ? (
              <Suspense fallback={<Skeleton className="h-60 w-full" />}>
                <Charts kind="price" data={overview.daily} />
              </Suspense>
            ) : (
              <p className="flex h-60 items-center justify-center text-sm text-muted-foreground" data-testid="chart-price-unpriced">
                {t('operateCancel:spend.unpriced')}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
      <DisruptionsTable overview={overview} />
      <OperatorsTable overview={overview} />
    </>
  );
}

/** The price card was unavailable for (part of) this report: say so once, above the figures. */
function UnpricedNotice({ testId }: { testId: string }) {
  const { t } = useTranslation('admin');
  return (
    <p className="flex items-center gap-2 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-300" role="status" data-testid={testId}>
      <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
      {t('operateCancel:spend.unpricedHint')}
    </p>
  );
}

function MetricCard({ overview, metric, value, children }: { overview: ReportsOverview; metric: ReportMetricKey; value: string; children?: ReactNode }) {
  const { t, i18n } = useTranslation('admin');
  const def = overview.definitions[metric];
  return (
    <Card data-testid={`metric-${metric}`}>
      <CardContent className="flex h-full flex-col gap-1 p-4">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{localized(def.label, i18n.language)}</p>
        <p className="text-2xl font-semibold tabular-nums" data-testid={`metric-${metric}-value`}>
          {value}
        </p>
        {children ? <div className="text-xs text-muted-foreground">{children}</div> : null}
        <p className="mt-auto border-t border-border pt-1 text-[11px] leading-snug text-muted-foreground" data-testid={`metric-${metric}-definition`}>
          <span className="font-medium">{t('reports.definitionLabel')}:</span> {localized(def.definition, i18n.language)}
          <span className="block text-[10px] opacity-70">{t('reports.source', { source: def.source })}</span>
        </p>
      </CardContent>
    </Card>
  );
}

function MetricCards({ overview }: { overview: ReportsOverview }) {
  const { t } = useTranslation('admin');
  const m = overview.totals;
  const unavailable = t('operateCancel:spend.unpriced');
  const price = (v: number | null) => priceText(v, overview.priced, unavailable);
  const perPaxTotal = m.price.perPassenger.totalUsd;
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" data-testid="metric-cards">
      <MetricCard overview={overview} metric="disruptions" value={String(m.disruptions)} />
      <MetricCard overview={overview} metric="passengers" value={String(m.passengers)}>
        {t('reports.cards.passengers.attended', { count: m.attended })} · {t('reports.cards.passengers.excluded', { count: m.excluded })}
      </MetricCard>
      <MetricCard
        overview={overview}
        metric="pricePerPassenger"
        value={perPaxTotal === null && !overview.priced ? unavailable : `${price(perPaxTotal)} ${t('reports.cards.price.perPax')}`}
      >
        {t('reports.cards.price.hotel')} {price(m.price.perPassenger.hotelUsd)} · {t('reports.cards.price.transport')} {price(m.price.perPassenger.transportUsd)} · {t('reports.cards.price.meal')} {price(m.price.perPassenger.mealUsd)}
        <span className="block">{t('reports.cards.price.total', { value: price(m.price.totalUsd) })}</span>
      </MetricCard>
      <MetricCard overview={overview} metric="allocationAccuracy" value={pct(m.accuracy.accuratePct)}>
        {t('reports.cards.accuracy.differences', { count: m.accuracy.withDifferences })}
      </MetricCard>
      <MetricCard overview={overview} metric="manualWork" value={String(m.manualWork.actions)}>
        {t('reports.cards.manualWork.operators', { count: m.manualWork.operators })} · {t('reports.cards.manualWork.perPax', { value: num(m.manualWork.actionsPerPassenger, 2) })}
        <span className="block">{t('reports.cards.manualWork.passengerDriven', { count: m.manualWork.passengerDriven.checkIn })}</span>
      </MetricCard>
      <MetricCard overview={overview} metric="providerWait" value={m.providerWait.known ? p50p95(m.providerWait.hotel, 's') : t('reports.cards.providerWait.unknown')}>
        {m.providerWait.known ? (
          <>
            {t('reports.cards.providerWait.hotel', { count: m.providerWait.hotel.count })} · {t('reports.cards.providerWait.transport', { value: p50p95(m.providerWait.transport, 's'), count: m.providerWait.transport.count })}
          </>
        ) : null}
      </MetricCard>
      <MetricCard overview={overview} metric="effectiveTime" value={p50p95(m.effectiveTime.minutesToCheckIn, 'min')}>
        {t('reports.cards.effectiveTime.coverage', { value: pct(m.effectiveTime.coveragePct), count: m.effectiveTime.checkedIn })}
      </MetricCard>
    </div>
  );
}

function ReconciledBadge({ value }: { value: boolean | null }) {
  const { t } = useTranslation('admin');
  if (value === null) return <span className="text-muted-foreground">{t('reports.reconciledShort.unknown')}</span>;
  return value ? (
    <span className="inline-flex items-center gap-1 text-emerald-700">
      <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> {t('reports.reconciledShort.yes')}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-amber-700">
      <AlertTriangle className="h-3.5 w-3.5" aria-hidden /> {t('reports.reconciledShort.no')}
    </span>
  );
}

function DisruptionsTable({ overview }: { overview: ReportsOverview }) {
  const { t, i18n } = useTranslation('admin');
  const [dossier, setDossier] = useState<{ urn: string; title: string } | null>(null);
  const unavailable = t('operateCancel:spend.unpriced');
  return (
    <Card>
      <CardContent className="p-4">
        <h2 className="mb-2 text-sm font-semibold">{t('reports.table.title')}</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="reports-disruptions">
            <thead>
              <tr className="border-b text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="py-1 pr-2">{t('reports.table.disruption')}</th>
                <th className="py-1 pr-2">{t('reports.table.declared')}</th>
                <th className="py-1 pr-2">{t('reports.table.status')}</th>
                <th className="py-1 pr-2 text-right">{t('reports.table.passengers')}</th>
                <th className="py-1 pr-2 text-right">{t('reports.table.totalUsd')}</th>
                <th className="py-1 pr-2 text-right">{t('reports.table.perPax')}</th>
                <th className="py-1 pr-2 text-right">{t('reports.table.accuracy')}</th>
                <th className="py-1 pr-2 text-right">{t('reports.table.actions')}</th>
                <th className="py-1 pr-2 text-right">{t('reports.table.p95')}</th>
                <th className="py-1 pr-2">{t('reports.table.reconciled')}</th>
                <th className="py-1" />
              </tr>
            </thead>
            <tbody>
              {overview.disruptions.map((d) => (
                <tr key={d.urn} className="border-b last:border-0" data-testid="reports-disruption-row">
                  <td className="py-1 pr-2">
                    <span className="block font-medium">{d.title}</span>
                    <span className="block text-xs text-muted-foreground">{d.flights.join(', ') || d.urn}</span>
                  </td>
                  <td className="py-1 pr-2 text-xs">{fmtDateTime(d.detectedAt || null, i18n.language)}</td>
                  <td className="py-1 pr-2 text-xs">{t(`reports.status.${d.status}`, { defaultValue: d.status })}</td>
                  <td className="py-1 pr-2 text-right tabular-nums">{d.metrics.attended}</td>
                  <td className="py-1 pr-2 text-right tabular-nums" data-testid="reports-row-price">{priceText(d.metrics.price.totalUsd, d.priced, unavailable)}</td>
                  <td className="py-1 pr-2 text-right tabular-nums">{priceText(d.metrics.price.perPassenger.totalUsd, d.priced, unavailable)}</td>
                  <td className="py-1 pr-2 text-right tabular-nums">{pct(d.metrics.accuracy.accuratePct)}</td>
                  <td className="py-1 pr-2 text-right tabular-nums">{d.metrics.manualWork.actions}</td>
                  <td className="py-1 pr-2 text-right tabular-nums">{num(d.metrics.effectiveTime.minutesToCheckIn.p95)}</td>
                  <td className="py-1 pr-2 text-xs">
                    <ReconciledBadge value={d.reconciledWithSummary} />
                  </td>
                  <td className="py-1 text-right">
                    <Button variant="outline" size="sm" className="h-7 gap-1 px-2 text-xs" onClick={() => setDossier({ urn: d.urn, title: d.title })} data-testid="dossier-button">
                      <FileText className="h-3.5 w-3.5" aria-hidden />
                      {t('reports.table.dossier')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {dossier ? <DisruptionDossierDialog urn={dossier.urn} title={dossier.title} onClose={() => setDossier(null)} /> : null}
      </CardContent>
    </Card>
  );
}

function OperatorsTable({ overview }: { overview: ReportsOverview }) {
  const { t, i18n } = useTranslation('admin');
  return (
    <Card>
      <CardContent className="p-4">
        <h2 className="mb-2 text-sm font-semibold">{t('reports.operators.title')}</h2>
        {overview.operators.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="reports-operators-empty">
            {t('reports.operators.empty')}
          </p>
        ) : (
          <table className="w-full text-sm" data-testid="reports-operators">
            <thead>
              <tr className="border-b text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="py-1 pr-2">{t('reports.operators.operator')}</th>
                <th className="py-1 pr-2 text-right">{t('reports.operators.total')}</th>
                <th className="py-1 pr-2 text-right">{t('reports.operators.disruptions')}</th>
                <th className="py-1 pr-2">{t('reports.operators.first')}</th>
                <th className="py-1">{t('reports.operators.last')}</th>
              </tr>
            </thead>
            <tbody>
              {overview.operators.map((o) => (
                <tr key={o.operatorUrn} className="border-b last:border-0">
                  <td className="py-1 pr-2">{o.name ?? o.operatorUrn.replace(/^urn:user:/, '')}</td>
                  <td className="py-1 pr-2 text-right tabular-nums">{o.total}</td>
                  <td className="py-1 pr-2 text-right tabular-nums">{o.disruptions}</td>
                  <td className="py-1 pr-2 text-xs">{fmtDateTime(o.firstAt, i18n.language)}</td>
                  <td className="py-1 text-xs">{fmtDateTime(o.lastAt, i18n.language)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
